import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  TrendingUp as SalesIcon,
  AccountBalance as BalanceIcon,
  ArrowBack as BackIcon,
} from "@mui/icons-material";
import ReportDataGrid from "../components/ReportDataGrid";
import reportService from "../services/reportService";
import { useNavigate } from "react-router-dom";
import image from "../assets/riyal.png";
import { formatDate } from "../utils/dateUtils";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/apiService";

// ── Reusable currency display ──────────────────────────────────────────────
const RiyalAmount = ({ value, sx = {} }) => (
  <Box
    component="span"
    sx={{ display: "inline-flex", alignItems: "center", gap: "3px", ...sx }}
  >
    <img
      src={image}
      alt="SAR"
      style={{
        height: "13px",
        width: "auto",
        verticalAlign: "middle",
        flexShrink: 0,
      }}
    />
    {Number(value ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}
  </Box>
);

const CustomerSalesReport = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isVatAdmin = currentUser?.role === "vatadmin";
  const { t, language } = useTranslation();
  const isRtl = language === "ar";
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filters, setFilters] = useState({
    customerId: "",
    startDate: new Date(new Date().getFullYear(), 0, 1),
    endDate: new Date(),
    tenantId: "all",
  });
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = {
        customerId: filters.customerId,
        startDate: filters.startDate?.toISOString().split("T")[0],
        endDate: filters.endDate?.toISOString().split("T")[0],
      };
      if (language === "ar" || language === "en") {
        params.language = language;
      }

      // For vatadmin: pass tenantIds when a specific company is selected
      if (isVatAdmin && filters.tenantId && filters.tenantId !== "all") {
        params.tenantIds = filters.tenantId;
      }

      const response = await reportService.getCustomerSales(params);

      if (response.success) {
        setReportData(response.data || []);
        setTotals(response.totals);
        setCustomers(response.customers || []);
        setSelectedCustomer(response.filters?.customerId || "");
      }
    } catch (error) {
      console.error("Error fetching customer sales report:", error);
      alert(t("Reports.Failed_To_Fetch_Customer_Sales"));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get("/vat-admin/my-companies", {
        params: { limit: "all" },
      });
      const records = res.data?.data ?? res.data ?? [];
      setCompanies(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    }
  };

  useEffect(() => {
    if (isVatAdmin) {
      fetchCompanies();
    }
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  const handleBack = () => {
    navigate("/reports");
  };

  // ── Columns ──────────────────────────────────────────────────────────────
  const columns = [
    {
      field: "customerName",
      headerName: t("Reports.Customer_Name"),
      flex: 1.2,
      minWidth: 200,
      sortable: true,
      resizable: true,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "customerCode",
      headerName: t("Reports.Customer_Code"),
      flex: 0.8,
      minWidth: 130,
      sortable: true,
      resizable: true,
      headerAlign: "left",
      align: "left",
    },
    ...(isVatAdmin
      ? [
          {
            field: "companyName",
            headerName: t("Common.Company_Name") || "Company Name",
            flex: 1,
            minWidth: 140,
            sortable: true,
            headerAlign: "left",
            align: "left",
          },
        ]
      : []),
    {
      field: "customerType",
      headerName: t("Reports.Type"),
      flex: 0.6,
      minWidth: 80,
      resizable: true,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const type = params.value;
        const color =
          type === "B2B"
            ? { bg: "#e3f2fd", text: "#1565c0" }
            : { bg: "#fff3e0", text: "#e65100" };
        return (
          <Chip
            label={type}
            size="small"
            sx={{
              bgcolor: color.bg,
              color: color.text,
              fontWeight: 600,
              fontSize: "0.7rem",
              height: "22px",
            }}
          />
        );
      },
    },
    {
      field: "invoiceCount",
      headerName: t("Reports.Invoices"),
      flex: 0.7,
      minWidth: 100,
      type: "number",
      resizable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "totalSales",
      headerName: t("Reports.Total_Sales"),
      flex: 1,
      minWidth: 150,
      type: "number",
      resizable: true,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <RiyalAmount value={params.value} sx={{ fontWeight: 700 }} />
      ),
    },
    {
      field: "totalVAT",
      headerName: t("Reports.VAT"),
      flex: 0.9,
      minWidth: 130,
      type: "number",
      resizable: true,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => <RiyalAmount value={params.value} />,
    },
    {
      field: "totalDiscount",
      headerName: t("Reports.Discount"),
      flex: 0.9,
      minWidth: 130,
      type: "number",
      resizable: true,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => <RiyalAmount value={params.value} />,
    },
    {
      field: "totalPaid",
      headerName: t("Reports.Paid"),
      flex: 0.9,
      minWidth: 130,
      type: "number",
      resizable: true,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => <RiyalAmount value={params.value} />,
    },
    {
      field: "totalBalance",
      headerName: t("Reports.Balance"),
      flex: 0.9,
      minWidth: 130,
      type: "number",
      resizable: true,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <RiyalAmount
          value={params.value}
          sx={{ color: params.value > 0 ? "error.main" : "inherit" }}
        />
      ),
    },
    {
      field: "lastInvoiceDate",
      headerName: t("Reports.Last_Invoice"),
      flex: 0.9,
      minWidth: 120,
      sortable: true,
      resizable: true,
      headerAlign: "left",
      align: "left",
      valueFormatter: (params) => formatDate(params.value),
    },
  ];

  // ── Summary cards ─────────────────────────────────────────────────────────
  const summaryCards = [
    {
      title: t("Reports.Total_Customers"),
      value: totals?.totalCustomers || 0,
      icon: <PersonIcon />,
      color: "#4361ee",
      format: (val) => val.toLocaleString(),
    },
    {
      title: t("Reports.Total_Invoices"),
      value: totals?.totalInvoices || 0,
      icon: <ReceiptIcon />,
      color: "#6a1b9a",
      format: (val) => val.toLocaleString(),
    },
    {
      title: t("Reports.Total_Sales"),
      value: totals?.totalSales || 0,
      icon: <SalesIcon />,
      color: "#2e7d32",
      isAmount: true,
    },
    {
      title: t("Reports.Total_Balance"),
      value: totals?.totalBalance || 0,
      icon: <BalanceIcon />,
      color: "#d32f2f",
      isAmount: true,
    },
  ];

  return (
    <Container
      maxWidth={false}
      sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}
    >
      {/* Header */}
      <Box sx={{ mb: 0.5, display: "flex", alignItems: "center" }}>
        <IconButton
          onClick={handleBack}
          sx={{
            mr: isRtl ? 0 : 1.5,
            ml: isRtl ? 1.5 : 0,
            bgcolor: "rgba(67, 97, 238, 0.1)",
          }}
        >
          <BackIcon sx={{ transform: isRtl ? "rotate(180deg)" : "none" }} />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="700" gutterBottom>
            {t("Reports.Customer_Sales_Report")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Reports.Customer_Sales_Description")}
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 0.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
        }}
      >
        <Typography
          variant="caption"
          fontWeight="600"
          sx={{ mb: 1, display: "block", fontSize: "0.75rem" }}
        >
          {t("Reports.Filters")}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={1} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
              <TextField
                fullWidth
                select
                label={t("Reports.Customer")}
                value={selectedCustomer || ""}
                onChange={(e) => {
                  const value = e.target.value || "";
                  setSelectedCustomer(value);
                  handleFilterChange("customerId", value);
                }}
                disabled={loading || customers.length === 0}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 0,
                    height: "36px",
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                  "& .MuiOutlinedInput-input": { padding: "8px 14px" },
                }}
              >
                <MenuItem value="">{t("Reports.All_Customers")}</MenuItem>
                {customers.map((customer, index) => {
                  return (
                    <MenuItem
                      key={`${customer.customerCode || customer._id || index}`}
                      value={customer.customerCode || customer._id || ""}
                    >
                      {customer.name || t("Reports.Unknown_Customer")}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
              <DatePicker
                label={t("Reports.Start_Date")}
                value={filters.startDate}
                onChange={(date) => handleFilterChange("startDate", date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                        height: "36px",
                        fontSize: "0.875rem",
                      },
                      "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                      "& .MuiOutlinedInput-input": { padding: "8px 14px" },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
              <DatePicker
                label={t("Reports.End_Date")}
                value={filters.endDate}
                onChange={(date) => handleFilterChange("endDate", date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                        height: "36px",
                        fontSize: "0.875rem",
                      },
                      "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                      "& .MuiOutlinedInput-input": { padding: "8px 14px" },
                    },
                  },
                }}
              />
            </Grid>
            {isVatAdmin && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label={t("Common.Company_Name") || "Company"}
                  value={filters.tenantId}
                  onChange={(e) =>
                    handleFilterChange("tenantId", e.target.value)
                  }
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                      height: "36px",
                      fontSize: "0.875rem",
                    },
                    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                    "& .MuiOutlinedInput-input": { padding: "8px 14px" },
                  }}
                >
                  <MenuItem value="all">
                    {t("Common.All_Companies") || "All Companies"}
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.tenantId} value={company.tenantId}>
                      {company.name || company.tenantId}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={isVatAdmin ? 3 : 3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
                sx={{
                  borderRadius: 0,
                  height: "36px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                }}
              >
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  t("Reports.Apply_Filters")
                )}
              </Button>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Paper>

      {/* Summary Cards */}
      {totals && (
        <Grid container spacing={1} sx={{ mb: 0.5 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0,
                  borderLeft: isRtl ? "none" : `4px solid ${card.color}`,
                  borderRight: isRtl ? `4px solid ${card.color}` : "none",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "6px",
                      bgcolor: `${card.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: isRtl ? 0 : 1.5,
                      ml: isRtl ? 1.5 : 0,
                    }}
                  >
                    {React.cloneElement(card.icon, {
                      sx: { color: card.color, fontSize: 20 },
                    })}
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      sx={{ fontSize: "1.1rem" }}
                    >
                      {card.isAmount ? (
                        <RiyalAmount
                          value={card.value}
                          sx={{ fontSize: "1.1rem", fontWeight: 700 }}
                        />
                      ) : (
                        card.format(card.value)
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Customer Sales DataGrid */}
      <ReportDataGrid
        title={t("Reports.Customer_Sales_Report")}
        data={reportData}
        columns={columns}
        loading={loading}
        onRefresh={fetchReport}
        exportFileName={`customer-sales-${new Date().toISOString().split("T")[0]}`}
        pageSize={25}
        height={700}
        getRowId={(row) =>
          row.customerCode || row.id || Math.random().toString()
        }
        totals={totals}
        dateRange={{ startDate: filters.startDate, endDate: filters.endDate }}
      />
    </Container>
  );
};

export default CustomerSalesReport;
